/**
 * MAR Dashboard Component
 * Displays medication administration records with missed/refused dose tracking
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Pill, CheckCircle, XCircle, Clock, AlertTriangle, Plus,
  Filter, Search, Calendar, User, FileText, Package
} from 'lucide-react';
import MARForm from './MARForm';
import PRNMedicationTracking from './PRNMedicationTracking';
import MedicationService from '../../services/medication/MedicationService';
import { useToast } from '../Common/ToastProvider';

const MARDashboard = ({ participantId, medication = null }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [marRecords, setMarRecords] = useState([]);
  const [showMARForm, setShowMARForm] = useState(false);
  const [showPRNTracking, setShowPRNTracking] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(medication);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [missedDoses, setMissedDoses] = useState([]);
  const [refusedDoses, setRefusedDoses] = useState([]);
  const medServiceRef = useRef(MedicationService);

  useEffect(() => {
    if (participantId) {
      loadMARRecords();
      loadMissedRefusedDoses();
    }
  }, [participantId, filterStatus]);

  const loadMARRecords = async () => {
    setLoading(true);
    try {
      const svc = medServiceRef.current;
      const today = new Date().toISOString();
      const result = participantId
        ? svc.getMARByResident(participantId, today)
        : { success: true, data: [] };
      if (result.success) {
        let filtered = result.data;
        if (filterStatus !== 'all') {
          filtered = filtered.filter(mar => mar.status === filterStatus);
        }
        if (searchQuery) {
          filtered = filtered.filter(mar =>
            (mar.drugName || mar.medicationName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (mar.participantName || '').toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        setMarRecords(filtered);
      }
    } catch (error) {
      console.error('Error loading MAR records:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMissedRefusedDoses = async () => {
    try {
      const svc = medServiceRef.current;
      if (participantId) {
        const allResult = svc.getMARByResident(participantId, new Date().toISOString());
        if (allResult.success) {
          setMissedDoses(allResult.data.filter((r) => r.status === 'withheld' || r.status === 'not_available'));
          setRefusedDoses(allResult.data.filter((r) => r.status === 'refused'));
        }
      }
    } catch (error) {
      console.error('Error loading missed/refused doses:', error);
    }
  };

  useEffect(() => {
    loadMARRecords();
  }, [searchQuery]);

  const handleSaveMAR = async (marRecord) => {
    try {
      const svc = medServiceRef.current;
      const result = svc.recordAdministration({
        medicationOrderId: marRecord.medicationId || marRecord.medicationOrderId || 'manual',
        residentId: participantId || marRecord.residentId || 'unknown',
        administeredBy: marRecord.administeredBy || 'current_user',
        administeredByName: marRecord.administeredByName || 'Current User',
        status: marRecord.status || 'given',
        notes: marRecord.notes || '',
        witnessedBy: marRecord.witnessedBy || null,
      });
      if (result.success) {
        setShowMARForm(false);
        await loadMARRecords();
        await loadMissedRefusedDoses();
        showToast('Medication administration recorded successfully', 'success');
      } else {
        showToast(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error saving MAR:', error);
      showToast('Error saving medication administration record', 'error');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'administered': 'bg-green-100 text-green-800 border-green-300',
      'missed': 'bg-orange-100 text-orange-800 border-orange-300',
      'refused': 'bg-red-100 text-red-800 border-red-300',
      'scheduled': 'bg-blue-100 text-blue-800 border-blue-300',
      'error': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'administered':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'missed':
        return <XCircle className="w-5 h-5 text-orange-600" />;
      case 'refused':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'scheduled':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  const stats = {
    total: marRecords.length,
    administered: marRecords.filter(m => m.status === 'administered').length,
    missed: missedDoses.length,
    refused: refusedDoses.length,
    scheduled: marRecords.filter(m => m.status === 'scheduled').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">Medication Administration Records</h3>
          <p className="text-sm text-gray-600 mt-1">
            {participantId ? `Records for participant` : 'All MAR records'}
          </p>
        </div>
        <div className="flex gap-2">
          {selectedMedication && (
            <button
              onClick={() => setShowPRNTracking(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Pill className="w-4 h-4" />
              PRN Tracking
            </button>
          )}
          <button
            onClick={() => {
              setSelectedMedication(null);
              setShowMARForm(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New MAR Record
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 border-2 border-gray-300 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-700 text-base font-bold uppercase tracking-wide">Total</span>
            <FileText className="w-6 h-6 text-gray-500" />
          </div>
          <div className="text-4xl font-bold text-gray-900">{stats.total}</div>
          <p className="text-xs text-gray-500 mt-1 font-medium">Records</p>
        </div>
        
        <div className="bg-white rounded-xl p-5 border-2 border-green-300 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-green-700 text-base font-bold uppercase tracking-wide">Given</span>
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-4xl font-bold text-green-600">{stats.administered}</div>
          <p className="text-xs text-green-600 mt-1 font-medium">Administered</p>
        </div>
        
        <div className="bg-white rounded-xl p-5 border-2 border-orange-300 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-orange-700 text-base font-bold uppercase tracking-wide">Missed</span>
            <AlertTriangle className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-4xl font-bold text-orange-600">{stats.missed}</div>
          <p className="text-xs text-orange-600 mt-1 font-medium">Not Given</p>
        </div>
        
        <div className="bg-white rounded-xl p-5 border-2 border-red-300 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-red-700 text-base font-bold uppercase tracking-wide">Refused</span>
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="text-4xl font-bold text-red-600">{stats.refused}</div>
          <p className="text-xs text-red-600 mt-1 font-medium">By Resident</p>
        </div>
        
        <div className="bg-white rounded-xl p-5 border-2 border-blue-300 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-blue-700 text-base font-bold uppercase tracking-wide">Pending</span>
            <Clock className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-4xl font-bold text-blue-600">{stats.scheduled}</div>
          <p className="text-xs text-blue-600 mt-1 font-medium">Scheduled</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search medications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="administered">Administered</option>
            <option value="missed">Missed</option>
            <option value="refused">Refused</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>
      </div>

      {/* Missed/Refused Alerts */}
      {(missedDoses.length > 0 || refusedDoses.length > 0) && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <h4 className="text-lg font-bold text-red-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Missed & Refused Doses
          </h4>
          <div className="space-y-3">
            {missedDoses.slice(0, 5).map(mar => (
              <div key={mar.id} className="bg-white rounded-lg p-4 border border-red-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{mar.medicationName}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Reason:</strong> {mar.missedReason}
                    </p>
                    {mar.missedNotes && (
                      <p className="text-sm text-gray-600 mt-1">{mar.missedNotes}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Scheduled: {mar.scheduledTime ? new Date(mar.scheduledTime).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
                    Missed
                  </span>
                </div>
              </div>
            ))}
            {refusedDoses.slice(0, 5).map(mar => (
              <div key={mar.id} className="bg-white rounded-lg p-4 border border-red-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{mar.medicationName}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Reason:</strong> {mar.refusedReason}
                    </p>
                    {mar.refusedNotes && (
                      <p className="text-sm text-gray-600 mt-1">{mar.refusedNotes}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Scheduled: {mar.scheduledTime ? new Date(mar.scheduledTime).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                    Refused
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MAR Records List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-bold text-gray-900">Administration Records</h4>
        </div>
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading records...</div>
          ) : marRecords.length === 0 ? (
            <div className="p-12 text-center">
              <Pill className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium text-gray-900 mb-2">No MAR Records</p>
              <p className="text-sm text-gray-500 mb-4">Create your first medication administration record</p>
              <button
                onClick={() => setShowMARForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create MAR Record
              </button>
            </div>
          ) : (
            marRecords.map(mar => (
              <div key={mar.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(mar.status)}
                      <span className="font-semibold text-gray-900">{mar.medicationName}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(mar.status)}`}>
                        {mar.status.toUpperCase()}
                      </span>
                      {mar.isPRN && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                          PRN
                        </span>
                      )}
                      {mar.barcode && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          Scanned
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                      <div>
                        <p className="text-gray-600">Dosage</p>
                        <p className="font-medium text-gray-900">{mar.dosage}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Route</p>
                        <p className="font-medium text-gray-900">{mar.route}</p>
                      </div>
                      {mar.actualAdministrationTime && (
                        <div>
                          <p className="text-gray-600">Administered</p>
                          <p className="font-medium text-gray-900">
                            {new Date(mar.actualAdministrationTime).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {mar.administeringWorkerName && (
                        <div>
                          <p className="text-gray-600">Worker</p>
                          <p className="font-medium text-gray-900">{mar.administeringWorkerName}</p>
                        </div>
                      )}
                    </div>
                    {mar.prnReason && (
                      <div className="mt-2 p-2 bg-purple-50 rounded-lg">
                        <p className="text-xs text-purple-800">
                          <strong>PRN Reason:</strong> {mar.prnReason}
                        </p>
                        {mar.prnSymptoms && (
                          <p className="text-xs text-purple-800 mt-1">
                            <strong>Symptoms:</strong> {mar.prnSymptoms}
                          </p>
                        )}
                      </div>
                    )}
                    {mar.missedReason && (
                      <div className="mt-2 p-2 bg-orange-50 rounded-lg">
                        <p className="text-xs text-orange-800">
                          <strong>Missed Reason:</strong> {mar.missedReason}
                        </p>
                        {mar.missedNotes && (
                          <p className="text-xs text-orange-800 mt-1">{mar.missedNotes}</p>
                        )}
                      </div>
                    )}
                    {mar.refusedReason && (
                      <div className="mt-2 p-2 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-800">
                          <strong>Refused Reason:</strong> {mar.refusedReason}
                        </p>
                        {mar.refusedNotes && (
                          <p className="text-xs text-red-800 mt-1">{mar.refusedNotes}</p>
                        )}
                      </div>
                    )}
                    {mar.outcomeObservations && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-600">Outcome:</p>
                        <p className="text-sm text-gray-900">{mar.outcomeObservations}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* MAR Form Modal */}
      {showMARForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Medication Administration Record</h3>
              <button
                onClick={() => setShowMARForm(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <MARForm
              participantId={participantId}
              medication={selectedMedication}
              onSave={handleSaveMAR}
              onCancel={() => setShowMARForm(false)}
            />
          </div>
        </div>
      )}

      {/* PRN Tracking Modal */}
      {showPRNTracking && selectedMedication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">PRN Medication Tracking</h3>
              <button
                onClick={() => setShowPRNTracking(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <PRNMedicationTracking
              participantId={participantId}
              medication={selectedMedication}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MARDashboard;
